import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import standardMetricsIcon from '../../../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../../../assets/additionalMetrics.png';

import GradientDefs, { MetricColors } from '../../../../components/metricMonthlySummaryCard/GradientDefs';
import { MetricMonthlySummaryCard } from '../../../../components/metricMonthlySummaryCard/MetricMonthlySummaryCard';
import MessageInfo from '../../../../components/messageInfo/MessageInfo';
import { useGetRollingMetricSummaryQuery } from '../../coachingActionSlice';
import { isTieredScorecard } from '../../../../utils/metricAttainment';
import type { RollingMetricSummary } from '../associateCAPMetrics-types';

interface PropTypes {
    /** Associate's employee number — required to fetch rolling metric data */
    empNo: string;
}

const MetricCardsList = memo(({
    metrics,
    selectedMetricName,
    onCardClick,
}: {
    metrics: RollingMetricSummary[];
    selectedMetricName: string;
    onCardClick: (metricName: string, index: number) => void;
}) => {
    if (metrics.length === 0) {
        return (
            <div className="col-12 ps-4 pb-4 h-100">
                <MessageInfo
                    title="No metrics found."
                    message="No metrics are available for this category."
                    rowHeight="208px"
                />
            </div>
        );
    }

    return (
        <div className="pe-0 ps-0" style={{ backgroundColor: '#FFFFFF' }}>
            <div className="d-flex flex-nowrap">
                {metrics.map((metric, index) => {
                    const tiered = isTieredScorecard(metric.scorecard_calculation_rule_id);
                    return (
                        <div className="flex-grow-1 flex-basis-0" key={metric.label}>
                            <MetricMonthlySummaryCard
                                targetVal={metric.target}
                                metricAttained={metric.metric_attained ?? undefined}
                                metricNameVal={metric.label}
                                metricTypeName={metric.metric_type_name}
                                metricVal={metric.metric_value ?? 0}
                                metricDesc={metric.metric_desc}
                                metricDirectionId={metric.metric_direction_id}
                                onCardClick={onCardClick}
                                selectedMetric={selectedMetricName}
                                index={index}
                                metricOmitted={metric.metric_omitted ?? undefined}
                                variant={tiered ? 'tiered' : 'legacy'}
                                attainmentRank={metric.attainment_rank}
                                attainmentLevelLabel={metric.metric_attainment_level_label}
                                targets={metric.targets}
                                min={metric.min ?? undefined}
                                max={metric.max ?? undefined}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

MetricCardsList.displayName = 'MetricCardsList';

const AssociateCAPMetrics = memo(({ empNo }: PropTypes) => {
    const [selectedMetricName, setSelectedMetricName] = useState<string>('');
    const [selectedMetricColorIndex, setSelectedMetricColorIndex] = useState(0);
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');

    const { data: rollingMetricData, isLoading, isError } = useGetRollingMetricSummaryQuery(empNo, {
        skip: !empNo,
    });

    const metrics: RollingMetricSummary[] = useMemo(
        () => rollingMetricData?.data ?? [],
        [rollingMetricData]
    );

    const payoutMetrics = useMemo(
        () => metrics.filter((m) => m.is_payout_metric === true),
        [metrics]
    );

    const nonPayoutMetrics = useMemo(
        () => metrics.filter((m) => m.is_payout_metric === false),
        [metrics]
    );

    // Auto-select first payout metric on load
    useEffect(() => {
        if (payoutMetrics.length > 0) {
            setSelectedMetricName(payoutMetrics[0].label);
            setSelectedMetricColorIndex(0);
        } else if (metrics.length > 0) {
            setSelectedMetricName(metrics[0].label);
            setSelectedMetricColorIndex(0);
        } else {
            setSelectedMetricName('');
            setSelectedMetricColorIndex(0);
        }
    }, [metrics]);

    const handleCardClick = useCallback((metricName: string, index: number) => {
        setSelectedMetricName(metricName);
        setSelectedMetricColorIndex(index);
    }, []);

    const selectedMetricBgColor = MetricColors[selectedMetricColorIndex ?? 0]?.bgColor ?? '';
    const scorecardName = metrics[0]?.scorecard_name ?? '';

    return (
        <>
            <GradientDefs />

            <div className="mt-3">
                <h6 className="fw-bold mb-3">My Performance</h6>
                <hr className="mt-0" />

                {scorecardName && (
                    <p className="fw-semibold mb-3">
                        Rolling 30 Day Metric Performance - {scorecardName}
                    </p>
                )}

                {isLoading && (
                    <div className="p-4 text-center text-muted">Loading metrics…</div>
                )}

                {isError && (
                    <div className="col-12 ps-4 pb-4">
                        <MessageInfo
                            title="Unable to load metric data."
                            message="An error occurred while fetching your performance metrics. Please try again later."
                            rowHeight="208px"
                        />
                    </div>
                )}

                {!isLoading && !isError && (
                    <div className="card shadow bg-body rounded border-1">
                        <div
                            className="d-flex flex-nowrap"
                            style={{ backgroundColor: selectedMetricBgColor, alignItems: 'stretch' }}
                        >
                            <div className="flex-grow-1 flex-basis-0" style={{ height: '100%' }}>
                                    <div className="p-3">
                                        {nonPayoutMetrics.length > 0 && (
                                        <div className="metrics-tab-switcher">
                                            <button
                                                className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                                                onClick={() => setActiveTab('standard')}
                                                type="button"
                                            >
                                                <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                                                Standard Metrics
                                            </button>
                                            <button
                                                className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''} ${nonPayoutMetrics.length === 0 ? 'metrics-tab--disabled' : ''}`}
                                                onClick={() => setActiveTab('additional')}
                                                disabled={nonPayoutMetrics.length === 0}
                                                type="button"
                                            >
                                                <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                                                Additional Metrics (Non Payout)
                                            </button>
                                        </div>
                                        )}
                                        <div className="metrics-tab-content">
                                            {activeTab === 'standard' && (
                                                <MetricCardsList
                                                    metrics={payoutMetrics}
                                                    selectedMetricName={selectedMetricName}
                                                    onCardClick={handleCardClick}
                                                />
                                            )}
                                            {activeTab === 'additional' && (
                                                <MetricCardsList
                                                    metrics={nonPayoutMetrics}
                                                    selectedMetricName={selectedMetricName}
                                                    onCardClick={handleCardClick}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
});

AssociateCAPMetrics.displayName = 'AssociateCAPMetrics';

export default AssociateCAPMetrics;